import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {makeApolloClientCall, makeApolloClientMutationCall} from "../../Api/makeApolloApiCall";
import {makeApiCall} from "../../Api/makeApiCall";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import ReactTable from "../Components/ReactTable";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import ConfirmationModal from "../Components/ConfirmationModal";
import {wrapValueInOptionStructure} from "../../utils/maybeAddLabelToOptions";
import {toastError, toastSuccess} from "../../utils/toast";
import {AVAILABLE_PROJECTION_VERSIONS_QUERY} from "./Components/VersionSelect";
import {NUMBER_EXPRESSION_COLUMN} from "./Components/storeUniverseTableHelpers";
import {useProjectionLoadTimer} from "./Components/ProjectionLoadTimer";
import {CELL_TARGET_UNIVERSE_OPTIONS, cellTargetUniverseParam} from "./cellTargets";
import {copyTargetVersionOptions} from "./ProjectionMaintenanceUniverseTab";
import {serverErrorMessage} from "./ProjectionMaintenanceProjectionTab";

export const COPY_PROJECTION_CELL_TARGETS_QUERY = `
    mutation($fromVersion: String!, $toVersion: String!, $universe: String!) {
        copyProjectionCellTargets(fromVersion: $fromVersion, toVersion: $toVersion, universe: $universe) {
            fromVersion
            toVersion
            universe
            overwritten
            versionCreated
        }
    }
`;

const cellTargetsUrl = (version, universe) =>
    `${process.env.REACT_APP_EDP_API_BASE_URL}/projectionCellTargets/?version=${encodeURIComponent(version)}&universe=${encodeURIComponent(cellTargetUniverseParam(universe))}`;

const universeLabel = (universe) =>
    CELL_TARGET_UNIVERSE_OPTIONS.find(option => option.value === universe)?.label ?? universe;

const cellTargetColumns = [
    {header: 'YEAR', accessorKey: 'year', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'CELL ID', accessorKey: 'cell_id', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'STORE COUNT', accessorKey: 'store_count', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'ACV', accessorKey: 'acv', ...NUMBER_EXPRESSION_COLUMN},
    {header: 'ACV ADJUSTED', accessorKey: 'acv_adjusted', ...NUMBER_EXPRESSION_COLUMN},
];

export const ProjectionMaintenanceCellTargetsTab = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [projectionVersionOptions, setProjectionVersionOptions] = useState(undefined);
    const [projectionVersion, setProjectionVersion] = useState(undefined);
    const [universe, setUniverse] = useState(undefined);

    const [submittedFilters, setSubmittedFilters] = useState(null);
    const {startLoad, finishLoad, cancelLoad} = useProjectionLoadTimer();

    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copyToVersion, setCopyToVersion] = useState('');
    const [copying, setCopying] = useState(false);
    const [targetHasCellTargets, setTargetHasCellTargets] = useState(false);

    useEffect(() => {
        makeApolloClientCall(AVAILABLE_PROJECTION_VERSIONS_QUERY)
            .then((projectionVersionsResponse) => {
                setProjectionVersionOptions(projectionVersionsResponse.availableProjectionVersions
                    .map(version => wrapValueInOptionStructure(version.version)));
            })
            .catch(() => setProjectionVersionOptions([]));
    }, []);

    useEffect(() => {
        if (!submittedFilters) return;

        const failedMessage = `Failed to load the cell targets for ${submittedFilters.universe} ${submittedFilters.projectionVersion}`;

        setLoading(true);
        startLoad('Cell Targets tab');
        makeApiCall(cellTargetsUrl(submittedFilters.projectionVersion, submittedFilters.universe))
            .then((response) => {
                if (response?.apiError) {
                    setData([]);
                    toastError(toast, failedMessage);
                    return;
                }
                finishLoad();
                setData(response?.data ?? []);
            })
            .catch(() => {
                setData([]);
                toastError(toast, failedMessage);
            })
            .finally(() => {
                cancelLoad();
                setLoading(false);
            });
    }, [submittedFilters]);

    const copyTargetOptions = copyTargetVersionOptions(projectionVersion, projectionVersionOptions ?? []);
    const isExistingVersion = (version) => (projectionVersionOptions ?? []).some(option => option.value === version);
    const creatingNewVersion = !!copyToVersion && !isExistingVersion(copyToVersion);

    useEffect(() => {
        setTargetHasCellTargets(false);
        if (!copyToVersion || !universe || creatingNewVersion) return undefined;

        let cancelled = false;
        makeApiCall(cellTargetsUrl(copyToVersion, universe))
            .then((response) => {
                if (!cancelled) setTargetHasCellTargets(!response?.apiError && (response?.data?.length ?? 0) > 0);
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [copyToVersion, universe, creatingNewVersion]);

    const closeCopyModal = () => {
        setCopyModalOpen(false);
        setCopyToVersion('');
    };

    const onCopyCellTargetsConfirmed = () => {
        const fromVersion = projectionVersion;
        const toVersion = copyToVersion;
        const label = universeLabel(universe);

        setCopying(true);
        makeApolloClientMutationCall(COPY_PROJECTION_CELL_TARGETS_QUERY, {fromVersion, toVersion, universe})
            .then((copyResponse) => {
                const {overwritten, versionCreated} = copyResponse.copyProjectionCellTargets;
                if (versionCreated) {
                    toastSuccess(toast, `Created ${toVersion} and copied the ${label} cell targets from ${fromVersion} into it`);
                    setProjectionVersionOptions(previousOptions => (previousOptions ?? []).some(option => option.value === toVersion)
                        ? previousOptions
                        : [...(previousOptions ?? []), wrapValueInOptionStructure(toVersion)]);
                } else {
                    toastSuccess(toast, overwritten
                        ? `Overwrote the ${label} cell targets in ${toVersion} with ${fromVersion}`
                        : `Copied the ${label} cell targets from ${fromVersion} to ${toVersion}`);
                }
                if (submittedFilters?.projectionVersion === toVersion && submittedFilters?.universe === universe)
                    setSubmittedFilters({projectionVersion: toVersion, universe});
                closeCopyModal();
            })
            .catch((error) => toastError(toast, `Failed to copy the ${label} cell targets from ${fromVersion} to ${toVersion}: ${serverErrorMessage(error)}`))
            .finally(() => setCopying(false));
    };

    return (
        <div className={"projection-maintenance-projection-contents"}>
            <div className={'projection-toolbar'} data-testid={'projection_factors_version'}>
                <SelectDropdown id={"projection_version_dropdown"}
                                className={"optional-dropdown"}
                                dataTestId={"projection_version"}
                                inputLabel={"Projection Version"}
                                selectValue={projectionVersion}
                                options={projectionVersionOptions}
                                onChange={(option) => setProjectionVersion(option?.value)}
                />

                <SelectDropdown id={"cell_targets_universe_dropdown"}
                                className={"optional-dropdown"}
                                dataTestId={"cell_targets_universe"}
                                inputLabel={"Universe"}
                                selectValue={universe}
                                selectLabel={CELL_TARGET_UNIVERSE_OPTIONS.find(option => option.value === universe)?.label}
                                options={CELL_TARGET_UNIVERSE_OPTIONS}
                                onChange={(option) => setUniverse(option?.value)}
                />

                <button type={'button'}
                        className={'button primary projection-summary-submit-button'}
                        data-testid={'cell-targets-submit-button'}
                        disabled={!projectionVersion || !universe}
                        title={projectionVersion && universe ? 'Load the cell targets for the selected version and universe' : 'Select a Projection Version and Universe first'}
                        onClick={() => setSubmittedFilters({projectionVersion, universe})}
                >
                    Submit
                </button>

                <button type={'button'}
                        className={'button primary projection-copy-version-button'}
                        data-testid={'copy-cell-targets-button'}
                        disabled={!projectionVersion || !universe}
                        title={projectionVersion && universe ? 'Copy the cell targets for the selected version and universe to another version' : 'Select a Projection Version and Universe first'}
                        onClick={() => setCopyModalOpen(true)}
                >
                    Copy Cell Targets
                </button>
            </div>

            {submittedFilters && <LoadingWrapper loading={loading}>
                <ReactTable className={'projection-cell-targets-table'}
                            filterable
                            data={data}
                            columns={cellTargetColumns}
                            initialState={{pagination: {pageSize: 25}}}
                />
            </LoadingWrapper>}

            {copyModalOpen && <ConfirmationModal headerText={'Copy Cell Targets'}
                                                 confirmButtonText={'Copy'}
                                                 loading={copying}
                                                 submitButtonDisabled={!copyToVersion}
                                                 confirmationCallback={onCopyCellTargetsConfirmed}
                                                 cancellationCallback={closeCopyModal}
            >
                {!creatingNewVersion && targetHasCellTargets &&
                    <p className={'copy-cell-to-market-warning'} data-testid={'copy-cell-targets-warning'}>
                        {`Cell targets for ${universeLabel(universe)} in ${copyToVersion} will be overwritten.`}
                    </p>}
                {creatingNewVersion &&
                    <p className={'copy-cell-to-market-warning'} data-testid={'copy-cell-targets-new-version-notice'}>
                        {`${copyToVersion} does not exist yet. It will be created as a new, empty version with only these cell targets.`}
                    </p>}
                <SelectDropdown id={'copy_cell_targets_version_dropdown'}
                                inputId={'copy_cell_targets_version_input'}
                                inputLabel={'Version'}
                                containerClassName={'copy-projection-version-field'}
                                dataTestId={'copy-cell-targets-version'}
                                isCreatable
                                options={copyTargetOptions}
                                selectValue={copyToVersion || undefined}
                                placeholder={'Select or type a new version...'}
                                formatCreateLabel={(input) => `Create new version "${input.trim()}"`}
                                isValidNewOption={(input) => {
                                    const version = input.trim();
                                    return !!version && version !== projectionVersion && !isExistingVersion(version);
                                }}
                                menuPortalTarget={document.body}
                                styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                                onChange={(option) => setCopyToVersion(option?.value?.trim() ?? '')}
                                onCreateOption={(input) => setCopyToVersion(input.trim())}
                />
            </ConfirmationModal>}
        </div>
    );
};
